import type {
  MbcBriefQuality,
  MbcBriefQualityCheck,
  MbcBriefQualityItem,
  MbcBriefQualityVariation,
} from "@nlc/shared-types";

import {
  AlertCircle,
  Check,
  ChevronDown,
  Circle,
  Clock,
  type LucideIcon,
  PlusCircle,
  X,
} from "lucide-react";
import { Button, Disclosure, DisclosurePanel } from "react-aria-components";

import { Skeleton } from "@/components/skeleton";
import { ScrollFade } from "@/features/request/mbc-chat/components/ScrollFade";
import { cn } from "@/utils/cn";

import { briefScoreSummary } from "../lib/brief-score-summary";

const VERDICT_LABELS: Record<
  NonNullable<MbcBriefQualityVariation["quality"]>,
  string
> = {
  bad: "Weak",
  good: "Good",
  neutral: "Could be better",
};

const VERDICT_DOT: Record<
  NonNullable<MbcBriefQualityVariation["quality"]>,
  string
> = {
  bad: "bg-utility-error-500",
  good: "bg-utility-success-500",
  neutral: "bg-utility-warning-500",
};

const STATUS_LABELS: Record<MbcBriefQualityItem["status"], string> = {
  bad: "Needs attention",
  good: "Passes",
  neutral: "Almost there",
  pending: "Not judged yet",
  unanswered: "Not answered yet",
};

function variationHeading({
  label,
  variation,
}: MbcBriefQualityVariation): string {
  if (variation === null) return "Whole brief";

  return label ? `Variation ${variation} - ${label}` : `Variation ${variation}`;
}

function CheckIcon({ pass }: { pass: boolean | null }) {
  const iconProps = { className: "mt-0.5 size-3.5 shrink-0", role: "img" };

  if (pass === null) {
    return (
      <Circle
        {...iconProps}
        aria-label="Not judged yet"
        className={cn(iconProps.className, "text-utility-gray-400")}
      />
    );
  }

  return pass ? (
    <Check
      {...iconProps}
      aria-label="Passes"
      className={cn(iconProps.className, "text-utility-success-500")}
    />
  ) : (
    <X
      {...iconProps}
      aria-label="Needs work"
      className={cn(iconProps.className, "text-utility-error-500")}
    />
  );
}

function CheckList({ checks }: { checks: MbcBriefQualityCheck[] }) {
  return (
    <ul className="flex flex-col gap-1">
      {checks.map((check) => (
        <li className="flex items-start gap-1.5 text-xs" key={check.key}>
          <CheckIcon pass={check.pass} />
          <div className="min-w-0">
            <p className="text-text-secondary">{check.label}</p>
            {check.pass === false && check.suggestion ? (
              <p className="text-text-tertiary">{check.suggestion}</p>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function allChecksPass(checks: MbcBriefQualityCheck[]): boolean {
  return checks.length > 0 && checks.every((check) => check.pass === true);
}

// Uncontrolled on purpose: the default is read on first render only, so a
// criterion that starts passing later never moves under the reader.
function Expandable({
  children,
  className,
  defaultExpanded,
  header,
}: {
  children: React.ReactNode;
  className?: string;
  defaultExpanded: boolean;
  header: React.ReactNode;
}) {
  return (
    <Disclosure className="group" defaultExpanded={defaultExpanded}>
      <Button
        className={cn(
          "flex w-full cursor-pointer items-center gap-1.5 rounded-sm text-left outline-hidden focus-visible:ring-2 focus-visible:ring-border-brand",
          className,
        )}
        slot="trigger"
      >
        {header}
        <ChevronDown
          aria-hidden
          className="ml-auto size-4 shrink-0 text-fg-quaternary transition-transform group-data-[expanded]:rotate-180"
        />
      </Button>
      <DisclosurePanel>{children}</DisclosurePanel>
    </Disclosure>
  );
}

function VariationLabel({
  variation,
}: {
  variation: MbcBriefQualityVariation;
}) {
  const { quality } = variation;

  return (
    <>
      <span
        aria-label={quality ? VERDICT_LABELS[quality] : "Not judged yet"}
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          quality ? VERDICT_DOT[quality] : "bg-utility-gray-400",
        )}
        role="img"
      />
      {variationHeading(variation)}
    </>
  );
}

function VariationChecklists({
  variations,
}: {
  variations: MbcBriefQualityVariation[];
}) {
  // A single design has no numbered blocks, so there is nothing to name.
  const showHeading = variations.length > 1;

  return (
    <div className="mt-1.5 flex flex-col gap-2">
      {variations.map((variation) => (
        <div key={variation.variation ?? "whole-brief"}>
          {showHeading ? (
            <Expandable
              className="py-0.5 text-xs font-medium text-text-secondary"
              defaultExpanded={!allChecksPass(variation.checks)}
              header={<VariationLabel variation={variation} />}
            >
              <div className="mt-1">
                <CheckList checks={variation.checks} />
              </div>
            </Expandable>
          ) : (
            <CheckList checks={variation.checks} />
          )}
        </div>
      ))}
    </div>
  );
}

const STATUS_ICONS: Record<MbcBriefQualityItem["status"], LucideIcon> = {
  bad: AlertCircle,
  good: Check,
  neutral: AlertCircle,
  pending: Clock,
  unanswered: PlusCircle,
};

const STATUS_ICON_COLORS: Record<MbcBriefQualityItem["status"], string> = {
  bad: "text-utility-warning-600",
  good: "text-utility-success-600",
  neutral: "text-utility-warning-600",
  pending: "text-fg-quaternary",
  unanswered: "text-fg-tertiary",
};

function StatusIcon({ status }: { status: MbcBriefQualityItem["status"] }) {
  const Icon = STATUS_ICONS[status];

  return (
    <Icon
      aria-label={STATUS_LABELS[status]}
      className={cn("size-5 shrink-0", STATUS_ICON_COLORS[status])}
      role="img"
    />
  );
}

function CriterionRow({ item }: { item: MbcBriefQualityItem }) {
  const label = (
    <>
      <StatusIcon status={item.status} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-text-primary">
          {item.label}
        </span>
        {item.required && !item.passes ? (
          <span className="block text-[13px] text-text-tertiary">Required</span>
        ) : null}
      </span>
    </>
  );

  if (item.variations.length === 0) {
    return <div className="flex items-center gap-3">{label}</div>;
  }

  return (
    <Expandable
      className="gap-3"
      defaultExpanded={
        !item.variations.every((variation) => allChecksPass(variation.checks))
      }
      header={label}
    >
      <div className="pl-8">
        <VariationChecklists variations={item.variations} />
      </div>
    </Expandable>
  );
}

function ReadyPill({ canSubmit }: { canSubmit: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-[5px] text-[13px] font-bold",
        canSubmit
          ? "border-utility-success-200 bg-utility-success-50 text-utility-success-700"
          : "border-utility-warning-200 bg-utility-warning-50 text-utility-warning-700",
      )}
    >
      {canSubmit ? <Check aria-hidden className="size-3.5" /> : null}
      {canSubmit ? "Ready to submit" : "Not ready yet"}
    </span>
  );
}

function CompleteSection({ items }: { items: MbcBriefQualityItem[] }) {
  return (
    <Disclosure className="group mt-4 border-t border-border-secondary">
      <Button
        className="flex h-11 w-full cursor-pointer items-center justify-between rounded-sm px-1 text-sm font-semibold text-text-secondary outline-hidden focus-visible:ring-2 focus-visible:ring-border-brand"
        slot="trigger"
      >
        Complete ({items.length})
        <ChevronDown
          aria-hidden
          className="size-4 transition-transform group-data-[expanded]:rotate-180"
        />
      </Button>
      <DisclosurePanel>
        <ul className="flex flex-col gap-3 px-1 pb-1">
          {items.map((item) => (
            <li key={item.id}>
              <CriterionRow item={item} />
            </li>
          ))}
        </ul>
      </DisclosurePanel>
    </Disclosure>
  );
}

type BriefQualityCardProps = {
  className?: string;
  /** The brief is changing (e.g. the agent is replying), so its score is not final. */
  isCalculating?: boolean;
  quality: MbcBriefQuality | null;
};

/** Shared between the chat page's sidebar and the review page. Each criterion
 * arrives already joined with its per-variation checklists — this only
 * renders. A null `quality` means the first brief request is still in flight. */
export function BriefQualityCard({
  className,
  isCalculating = false,
  quality,
}: BriefQualityCardProps) {
  const {
    color: headerColor,
    isJudging,
    notStarted,
  } = briefScoreSummary(quality);
  const calculating = isCalculating || isJudging;
  const score = !quality || notStarted ? 0 : quality.score;
  const toImprove = quality?.items.filter((item) => !item.passes) ?? [];
  const complete = quality?.items.filter((item) => item.passes) ?? [];

  return (
    <section
      className={cn(
        "flex min-h-0 flex-col rounded-2xl border border-border-secondary bg-bg-primary p-6 shadow-xs",
        className,
      )}
    >
      <h3 className="shrink-0 text-lg font-bold text-text-primary">
        Brief quality
      </h3>

      {quality ? (
        <>
          {calculating ? (
            <p
              className="mt-4 shrink-0 text-2xl font-bold text-text-tertiary"
              role="status"
            >
              Calculating...
            </p>
          ) : (
            <>
              <div className="mt-4 flex shrink-0 items-center justify-between">
                <span
                  className={cn(
                    "text-[44px] leading-none font-bold tracking-tight",
                    notStarted && "text-text-tertiary",
                  )}
                  style={{ color: headerColor }}
                >
                  {score}%
                </span>
                {notStarted ? (
                  <span className="text-sm font-semibold text-text-tertiary">
                    Not started
                  </span>
                ) : (
                  <ReadyPill canSubmit={quality.canSubmit} />
                )}
              </div>
              <div className="mt-4 h-2.5 shrink-0 overflow-hidden rounded-full bg-utility-gray-200">
                <div
                  className="h-full rounded-full bg-bg-brand-primary"
                  style={{ backgroundColor: headerColor, width: `${score}%` }}
                />
              </div>
            </>
          )}
          <p className="mt-3.5 shrink-0 text-sm text-text-tertiary">
            {quality.canSubmit
              ? "You can submit now. Adding the details below gives your designers more to work with."
              : "Complete the items below to submit your brief."}
          </p>

          <ScrollFade className="mt-5 flex-1">
            {toImprove.length > 0 ? (
              <>
                <h4 className="text-xs font-semibold tracking-wide text-text-tertiary uppercase">
                  Add to improve
                </h4>
                <ul className="mt-2 flex flex-col gap-2">
                  {toImprove.map((item) => (
                    <li
                      className="rounded-xl border border-border-secondary bg-bg-primary p-3"
                      key={item.id}
                    >
                      <CriterionRow item={item} />
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {complete.length > 0 ? <CompleteSection items={complete} /> : null}
          </ScrollFade>
        </>
      ) : (
        <div
          aria-label="Loading brief quality"
          className="mt-5 flex min-h-0 flex-col gap-3 overflow-y-auto"
          role="status"
        >
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      )}
    </section>
  );
}
